import React, {
    type Dispatch,
    type SetStateAction,
    useEffect,
    useMemo,
    useState,
} from "react"

import moment from "moment-timezone"
import { useFormContext } from "react-hook-form"

import {
    AvailableInsuranceType,
    type AvailablePlanType,
    BlueCardEligibility,
    CoverageCheckResponseType,
} from "@common-shared/config/coverage.config.ts"
import { CoverageHistoryMode } from "@common-shared/config/coverageHistory.config.ts"
import { FeatureFlag } from "@common-shared/config/featureFlags.config.ts"
import {
    AgenticCheckStatus,
    OneVerifyFlagType,
} from "@common-shared/config/oneVerify/oneVerifyCoverage.config.ts"
import {
    PoliciesListType,
    PolicyAssignability,
    PolicyAssignmentAppointmentType,
    PolicyAssignmentStatus,
    PolicyAssignmentWorkflowStatus,
    PolicyCredentialingStatus,
} from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"
import { IPoliciesListFiltersData } from "@common-shared/types/policies.types.ts"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config.ts"
import { AutoCompleteElement } from "@front-shared/elements/autoComplete.element.tsx"
import { CheckboxElement } from "@front-shared/elements/checkbox.element.tsx"
import { DatePickerElement } from "@front-shared/elements/datePicker.element.tsx"
import { InputElement } from "@front-shared/elements/input.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import { SelectElement } from "@front-shared/elements/select.element.tsx"
import { TagsSelectElement } from "@front-shared/elements/tagsSelect.element.tsx"
import { LookupService } from "@front-shared/services/lookup.service.ts"
import type {
    ISelectRawOption,
    ISelectRenderedOption,
} from "@front-shared/types/objects.types"

import { CHECKER_CONFIG } from "../../../config/checker.config"
import { COVERAGE_CONFIG } from "../../../config/coverage.config"
import { GENERAL_CONFIG } from "../../../config/general.config"
import { POLICIES_CONFIG } from "../../../config/policies.config"
import { TOOLTIPS_CONFIG } from "../../../config/tooltips.config"
import { RelativeDateRangeInputElement } from "../../../elements/relativeDateRangeInput.element"
import { LookupHelper } from "../../../helpers/lookup.helper"
import { PermissionsHelper } from "../../../helpers/permissions.helper"
import { PoliciesHelper } from "../../../helpers/policies.helper.ts"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook.ts"
import { useIsFeatureFlagEnabled } from "../../../hooks/isFeatureFlagEnabled.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { organizationSelector } from "../../../store/selectors/organization.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import type { IPermissionBasedFilterConfig } from "../../../types/objects.types.ts"

import expandFiltersIcon from "@front-shared/assets/images/icons/plus-reports-filters.svg"

export const PoliciesFiltersFormComponent: React.FunctionComponent<
    IPoliciesFiltersFormComponentProps
> = props => {
    const {
        formState: { errors },
        control,
        register,
        unregister,
        setValue,
        watch,
    } = useFormContext<IPoliciesListFiltersData>()

    const dataWatcher = watch()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const organizationState = useAppSelector(organizationSelector)

    const canManageTaskAssignments = useHasPermissions(
        OneVerifyPermission.manageTaskAssignments,
        undefined,
        false,
    )

    const isBillerSelfAssignEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_BILLER_SELF_ASSIGN,
        undefined,
        undefined,
    )

    const showFilterByCheckUsers = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_SHOW_FILTER_BY_CHECK_USERS,
        undefined,
        undefined,
    )

    const canFilterByCheckUsers = useHasPermissions(
        OneVerifyPermission.filterByOrganizationUsers,
        undefined,
        false,
    )

    const [maxStartDate] = useState<Date>(moment().toDate())

    const [additionalFiltersExpanded, setAdditionalFiltersExpanded] =
        useState<boolean>(
            (() => {
                // if some of the additional filters are set, then show them expanded by default
                return [
                    ...POLICIES_CONFIG.listTypeFilters[props.listType]
                        .additional,
                    ...(POLICIES_CONFIG.listTypeFilters[props.listType]
                        .additionalWide || []),
                ].some(filterKey => {
                    // filter config values may be strings or objects; see IPolicyListFilterConfig
                    return !!props.existingFilters[
                        typeof filterKey === "string"
                            ? filterKey
                            : filterKey.key
                    ]
                })
            })(),
        )

    const availableFlagOptions = useMemo<ISelectRawOption[]>(() => {
        const flagTypes = props.listType
            ? POLICIES_CONFIG.flagTypesByViewType[props.listType]
            : Object.values(OneVerifyFlagType)

        return flagTypes.map(flag => {
            return {
                label: POLICIES_CONFIG.flagTypeMappings[flag].label,
                value: flag,
            }
        })
    }, [props.listType])

    const availablePlanTypeOptions = useMemo<ISelectRenderedOption[]>(() => {
        return LookupHelper.getRenderedOptionsFromObject<AvailablePlanType>(
            props.listType
                ? CHECKER_CONFIG.planTypeMappingByViewType[props.listType]
                : CHECKER_CONFIG.planTypeMapping,
        )
    }, [props.listType])

    const availableInsuranceTypeOptions = useMemo<
        ISelectRenderedOption[]
    >(() => {
        return LookupHelper.getRenderedOptionsFromObjectMapped<AvailableInsuranceType>(
            COVERAGE_CONFIG.availableInsuranceTypeMapping,
            COVERAGE_CONFIG.unsupportedInsuranceTypes,
        )
    }, [])

    const additionalFiltersAlwaysExpanded = useMemo<boolean>(() => {
        return !!(props.additionalFiltersAlwaysExpanded || props.isRequired)
    }, [props.additionalFiltersAlwaysExpanded, props.isRequired])

    const filtersConfig = useMemo(() => {
        return POLICIES_CONFIG.listTypeFilters[props.listType]
    }, [props.listType])

    // Set default values (objects) in form - need to do for autoCompletes and TagSelects
    useEffect(() => {
        // AUTOCOMPLETEs
        props.existingFilters.payer &&
            setValue("payer", props.existingFilters.payer)

        // TAGS SELECTs
        setValue("responseType", props.existingFilters.responseType || [])

        setValue(
            "taskAssignmentStatus",
            props.existingFilters.taskAssignmentStatus || [],
        )

        setValue(
            "taskAssignmentAssignee",
            props.existingFilters.taskAssignmentAssignee || [],
        )

        setValue(
            "taskAssignmentAssignedBy",
            props.existingFilters.taskAssignmentAssignedBy || [],
        )

        setValue(
            "customFieldCredentialingStatus",
            props.existingFilters.customFieldCredentialingStatus || [],
        )

        if (showFilterByCheckUsers && canFilterByCheckUsers) {
            setValue(
                "checkedByUsers",
                props.existingFilters.checkedByUsers || [],
            )
        }
    }, [JSON.stringify(props.existingFilters)])

    const availableOrganizationUsers = useMemo<ISelectRawOption[]>(() => {
        return organizationState.users.map(user => {
            return {
                label: user.name,
                value: user.id,
            }
        })
    }, [organizationState.users])

    // We need to treat taskAssignmentsUnassigned as a controlled input via the default value
    const [taskAssignmentsUnassigned, setTaskAssignmentsUnassigned] =
        useState<boolean>(
            props.existingFilters.taskAssignmentsUnassigned || false,
        )

    const getRenderedFormFields = (
        filterConfigs: (
            | keyof IPoliciesListFiltersData
            | IPermissionBasedFilterConfig
        )[],
        withExtraMarginTopFloatingLabel = false,
    ) => {
        return (
            <>
                {filterConfigs.map((filterConfig, index) => {
                    const itemClassName = "mt-[24px]"

                    const itemFloatingLabelClassName =
                        withExtraMarginTopFloatingLabel
                            ? "mt-[40px]"
                            : itemClassName

                    // filter config values may be strings or objects; see IPolicyListFilterConfig
                    const neededFilterKey =
                        typeof filterConfig === "string"
                            ? filterConfig
                            : filterConfig.key

                    // If the filter is a permission based filter and the user does not have the permission,
                    // then we will not render the filter
                    if (
                        typeof filterConfig !== "string" &&
                        filterConfig.neededPermissions.length &&
                        !PermissionsHelper.hasPermission(
                            filterConfig.neededPermissions,
                            selectedPracticeRole,
                            filterConfig.neededPermissionsLogicalOperator,
                        )
                    ) {
                        return null
                    }

                    switch (neededFilterKey) {
                        case "checkDate":
                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="checkDate"
                                    defaultValue={
                                        props.existingFilters.checkDate
                                    }
                                    placeholder="Select date(s)"
                                    labelPrefix="Search"
                                    labelBoldPart="eligibility check(s)"
                                    labelSuffix="run through Nirvana..."
                                    selectValidations={{
                                        required:
                                            FRONT_VALIDATION_CONFIG.required,
                                    }}
                                    customRangeDateToSetToday={
                                        PoliciesHelper.getHistoryModeBasedOnListType(
                                            props.listType,
                                        ) ===
                                        CoverageHistoryMode.LATEST_COVERAGE
                                    }
                                    pastDatesAllowed
                                />
                            )

                        case "customFieldAppointmentCreatedTimestamp":
                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="customFieldAppointmentCreatedTimestamp"
                                    defaultValue={
                                        props.existingFilters
                                            .customFieldAppointmentCreatedTimestamp
                                    }
                                    placeholder="Select date(s)"
                                    labelPrefix="With"
                                    labelBoldPart="entry"
                                    labelSuffix="date(s)…"
                                    allowClear
                                    pastDatesAllowed
                                />
                            )

                        case "responseType":
                            return (
                                <TagsSelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    placeholder="Select as many as needed"
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .responseType
                                    }
                                    name="responseType"
                                    onSelected={(items: string[]) => {
                                        setValue(
                                            "responseType",
                                            items as CoverageCheckResponseType[],
                                        )
                                    }}
                                    maxTagCount={3}
                                    options={
                                        Object.values(
                                            CoverageCheckResponseType,
                                        ).map(value => ({
                                            label: COVERAGE_CONFIG
                                                .responseTypeMapping[value],
                                            value,
                                        })) || []
                                    }
                                    externalValue={
                                        dataWatcher.responseType
                                            ? Array.isArray(
                                                  dataWatcher.responseType,
                                              )
                                                ? dataWatcher.responseType
                                                : [dataWatcher.responseType]
                                            : undefined
                                    }
                                />
                            )

                        case "nextAppointmentDate":
                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="nextAppointmentDate"
                                    defaultValue={
                                        props.existingFilters
                                            .nextAppointmentDate
                                    }
                                    placeholder="Select date(s)"
                                    labelPrefix="With"
                                    labelBoldPart="next appointment"
                                    labelSuffix="date(s)…"
                                    allowClear
                                    futureDatesAllowed
                                />
                            )

                        case "planEndDate":
                            const isRegularLabel =
                                props.listType ===
                                PoliciesListType.HISTORY_SEARCH

                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="planEndDate"
                                    defaultValue={
                                        props.existingFilters.planEndDate
                                    }
                                    placeholder="Select date(s)"
                                    regularLabel={
                                        isRegularLabel
                                            ? POLICIES_CONFIG
                                                  .filtersDisplayMapping
                                                  .planEndDate
                                            : undefined
                                    }
                                    labelPrefix={
                                        !isRegularLabel ? "With" : undefined
                                    }
                                    labelBoldPart={
                                        !isRegularLabel ? "plan end" : undefined
                                    }
                                    labelSuffix={
                                        !isRegularLabel ? "date(s)…" : undefined
                                    }
                                    allowClear
                                    pastDatesAllowed
                                    futureDatesAllowed
                                />
                            )

                        case "overrideExpirationDate":
                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="overrideExpirationDate"
                                    defaultValue={
                                        props.existingFilters
                                            .overrideExpirationDate
                                    }
                                    placeholder="Select date(s)"
                                    labelPrefix="With"
                                    labelBoldPart="override expiration"
                                    labelSuffix="date(s)…"
                                    allowClear
                                    pastDatesAllowed
                                    futureDatesAllowed
                                />
                            )

                        case "flags":
                            return props.setSelectedFlags ? (
                                <div key={index} className={itemClassName}>
                                    <PrimaryText className="mb-[4px]">
                                        With the following{" "}
                                        <span className="text-bold">
                                            {POLICIES_CONFIG.filtersDisplayMapping.flags?.toLowerCase()}
                                            :
                                        </span>
                                    </PrimaryText>

                                    <TagsSelectElement
                                        placeholder="Select as many as needed"
                                        name="flags"
                                        onSelected={(items: string[]) => {
                                            props.setSelectedFlags?.(
                                                items as OneVerifyFlagType[],
                                            )
                                        }}
                                        maxTagCount={
                                            props.flagsDisplayedAmount || 1
                                        }
                                        options={availableFlagOptions}
                                        externalValue={props.selectedFlags}
                                        optionsFetcherDebounceMilliseconds={250}
                                    />
                                </div>
                            ) : null

                        case "patientId":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .patientId
                                    }
                                    name="patientId"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.patientId
                                    }
                                    isLabelStatic
                                />
                            )

                        case "patientType":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .patientType
                                    }
                                    name="patientType"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={Object.values(
                                        COVERAGE_CONFIG.selectRenderedPatientTypes,
                                    )}
                                    defaultValue={
                                        props.existingFilters.patientType
                                            ? COVERAGE_CONFIG
                                                  .selectRenderedPatientTypes[
                                                  props.existingFilters
                                                      .patientType
                                              ].displayValue
                                            : undefined
                                    }
                                />
                            )

                        case "groupId":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .groupId
                                    }
                                    name="groupId"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        minLength:
                                            FRONT_VALIDATION_CONFIG.minLength,
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={props.existingFilters.groupId}
                                    isLabelStatic
                                />
                            )

                        case "groupName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .groupName
                                    }
                                    name="groupName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        minLength:
                                            FRONT_VALIDATION_CONFIG.minLength,
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.groupName
                                    }
                                    isLabelStatic
                                />
                            )

                        case "planType":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .planType
                                    }
                                    name="planType"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={availablePlanTypeOptions}
                                    defaultValue={
                                        props.existingFilters.planType
                                            ? CHECKER_CONFIG.planTypeMapping[
                                                  props.existingFilters.planType
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "planName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .planName
                                    }
                                    name="planName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        minLength:
                                            FRONT_VALIDATION_CONFIG.minLength,
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.planName
                                    }
                                    isLabelStatic
                                />
                            )

                        case "payer":
                            return (
                                <div
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                >
                                    <AutoCompleteElement
                                        name="payer"
                                        label={
                                            POLICIES_CONFIG
                                                .filtersDisplayMapping.payer
                                        }
                                        tooltip={TOOLTIPS_CONFIG.checker.payer}
                                        dataFetcher={(search?: string) =>
                                            LookupService.lookupPayers(
                                                search,
                                                undefined,
                                                undefined,
                                                props.listType ===
                                                    PoliciesListType.AGENTIC
                                                    ? {
                                                          isAgenticPhoneSupported: true,
                                                          isAgenticPortalSupported: true,
                                                      }
                                                    : undefined,
                                            )
                                        }
                                        itemRenderer={
                                            LookupHelper.getRenderedPayer
                                        }
                                        reactHookFormErrors={errors}
                                        reactHookFormRegister={register}
                                        reactHookFormUnregister={unregister}
                                        reactHookFormSet={setValue}
                                        debounceMilliseconds={250}
                                        defaultValue={
                                            props.existingFilters.payer
                                                ? LookupHelper.getRenderedPayer(
                                                      props.existingFilters
                                                          .payer,
                                                  ).displayValue
                                                : undefined
                                        }
                                        shouldValidate
                                        isLabelStatic
                                        ignoreAutoSelectionOnBlur
                                        clearOnEmptySearch
                                        notFoundTitle="Don’t see your payer?"
                                        notFoundSubtitle={`
                                            <a
                                                href="mailto:${GENERAL_CONFIG.supportEmail}?subject=Insurance provider not found"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                Contact Support
                                            </a>
                                        `}
                                    />

                                    <a
                                        href={
                                            GENERAL_CONFIG.supportedInsurersUrl
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        className="!no-underline"
                                    >
                                        <PrimaryText
                                            className="mt-[8px]"
                                            typography="caption"
                                        >
                                            View all supported payers
                                        </PrimaryText>
                                    </a>
                                </div>
                            )

                        case "insuranceType":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .insuranceType
                                    }
                                    name="insuranceType"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={
                                        availableInsuranceTypeOptions
                                    }
                                    defaultValue={
                                        props.existingFilters.insuranceType
                                    }
                                />
                            )

                        case "bluecardEligibility":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .bluecardEligibility
                                    }
                                    name="bluecardEligibility"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={Object.values(
                                        BlueCardEligibility,
                                    ).map(value => ({
                                        value: value,
                                        displayValue:
                                            COVERAGE_CONFIG
                                                .bluecardEligibilityMapping[
                                                value
                                            ],
                                    }))}
                                    defaultValue={
                                        props.existingFilters
                                            .bluecardEligibility
                                            ? COVERAGE_CONFIG
                                                  .bluecardEligibilityMapping[
                                                  props.existingFilters
                                                      .bluecardEligibility
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "officeId":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .officeId
                                    }
                                    name="officeId"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.officeId
                                    }
                                    isLabelStatic
                                    allowClear={true}
                                />
                            )

                        case "primaryCarePhysicianName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .primaryCarePhysicianName
                                    }
                                    name="primaryCarePhysicianName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        minLength:
                                            FRONT_VALIDATION_CONFIG.minLength,
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters
                                            .primaryCarePhysicianName
                                    }
                                    isLabelStatic
                                />
                            )

                        case "primaryCarePhysicianNpi":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .primaryCarePhysicianNpi
                                    }
                                    name="primaryCarePhysicianNpi"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        pattern: FRONT_VALIDATION_CONFIG.npi,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters
                                            .primaryCarePhysicianNpi
                                    }
                                    isLabelStatic
                                />
                            )

                        case "primaryCarePhysicianOrganization":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .primaryCarePhysicianOrganization
                                    }
                                    name="primaryCarePhysicianOrganization"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        minLength:
                                            FRONT_VALIDATION_CONFIG.minLength,
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters
                                            .primaryCarePhysicianOrganization
                                    }
                                    isLabelStatic
                                />
                            )

                        case "resetBenefitsStatus":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .resetBenefitsStatus
                                    }
                                    name="resetBenefitsStatus"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={Object.values(
                                        COVERAGE_CONFIG.selectRenderedResetBenefitStatus,
                                    )}
                                    defaultValue={
                                        props.existingFilters
                                            .resetBenefitsStatus
                                            ? COVERAGE_CONFIG
                                                  .selectRenderedResetBenefitStatus[
                                                  props.existingFilters
                                                      .resetBenefitsStatus
                                              ].displayValue
                                            : undefined
                                    }
                                />
                            )

                        case "modalityCode":
                            const renderedModalityCodes =
                                LookupHelper.getRenderedModalityCodes(
                                    selectedPracticeRole?.availableModalityCodes,
                                )

                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .modalityCode
                                    }
                                    name="modalityCode"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={renderedModalityCodes}
                                    defaultValue={
                                        props.existingFilters.modalityCode
                                            ? renderedModalityCodes.find(
                                                  item =>
                                                      item.value ===
                                                      props.existingFilters
                                                          .modalityCode,
                                              )?.displayValue
                                            : undefined
                                    }
                                />
                            )

                        case "agenticCheckStatus":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .agenticCheckStatus
                                    }
                                    name="agenticCheckStatus"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={Object.values(
                                        AgenticCheckStatus,
                                    )
                                        .filter(
                                            status =>
                                                status !==
                                                AgenticCheckStatus.NEW,
                                        )
                                        .map(status => ({
                                            value: status,
                                            displayValue:
                                                POLICIES_CONFIG
                                                    .agenticCheckStatusMapping[
                                                    status
                                                ],
                                        }))}
                                    defaultValue={
                                        props.existingFilters.agenticCheckStatus
                                            ? POLICIES_CONFIG
                                                  .agenticCheckStatusMapping[
                                                  props.existingFilters
                                                      .agenticCheckStatus
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "memberId":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .memberId
                                    }
                                    name="memberId"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.memberId
                                    }
                                    isLabelStatic
                                />
                            )

                        case "providerName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .providerName
                                    }
                                    name="providerName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.providerName
                                    }
                                    isLabelStatic
                                    allowClear={true}
                                />
                            )

                        case "taskAssignmentDateWorked":
                            return (
                                <RelativeDateRangeInputElement
                                    key={index}
                                    className={itemClassName}
                                    inputRootKey="taskAssignmentDateWorked"
                                    defaultValue={
                                        props.existingFilters
                                            .taskAssignmentDateWorked
                                    }
                                    placeholder="Select date(s)"
                                    labelPrefix="With"
                                    labelBoldPart="last update"
                                    allowClear={true}
                                    pastDatesAllowed
                                />
                            )

                        case "taskAssignmentAssignee":
                            // If user does not have canManageTaskAssignments permission and isBillerSelfAssignEnabled flag is not enabled, then don't show the taskAssignmentAssignee element
                            if (
                                !isBillerSelfAssignEnabled &&
                                !canManageTaskAssignments
                            ) {
                                return null
                            }

                            return (
                                <div
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                >
                                    {canManageTaskAssignments ? (
                                        <TagsSelectElement
                                            placeholder="Select as many as needed"
                                            label={
                                                POLICIES_CONFIG
                                                    .filtersDisplayMapping
                                                    .taskAssignmentAssignee
                                            }
                                            name="taskAssignmentAssignee"
                                            onSelected={(items: string[]) => {
                                                setValue(
                                                    "taskAssignmentAssignee",
                                                    items.map(item => +item),
                                                )
                                            }}
                                            maxTagCount={1}
                                            options={availableOrganizationUsers}
                                            disabled={
                                                dataWatcher.taskAssignmentsUnassigned ||
                                                dataWatcher.taskAssignmentAssignability ===
                                                    PolicyAssignability.UNASSIGNABLE
                                            }
                                            externalValue={
                                                dataWatcher.taskAssignmentAssignability ===
                                                PolicyAssignability.UNASSIGNABLE
                                                    ? []
                                                    : dataWatcher.taskAssignmentsUnassigned
                                                      ? []
                                                      : (dataWatcher.taskAssignmentAssignee as
                                                            | string[]
                                                            | undefined)
                                            }
                                        />
                                    ) : null}

                                    {!POLICIES_CONFIG.assignedTasksListTypes.includes(
                                        props.listType,
                                    ) ? (
                                        <CheckboxElement
                                            className={
                                                canManageTaskAssignments
                                                    ? "mt-[4px]"
                                                    : "mt-[-12px]"
                                            }
                                            name="taskAssignmentsUnassigned"
                                            reactHookFormRegister={
                                                register as any
                                            }
                                            reactHookFormUnregister={
                                                unregister as any
                                            }
                                            reactHookFormSet={setValue as any}
                                            watchDefaultCheckedChange
                                            disabled={
                                                dataWatcher.taskAssignmentAssignability ===
                                                PolicyAssignability.UNASSIGNABLE
                                            }
                                            defaultChecked={
                                                dataWatcher.taskAssignmentAssignability ===
                                                PolicyAssignability.UNASSIGNABLE
                                                    ? false
                                                    : taskAssignmentsUnassigned
                                            }
                                            onChange={newValue => {
                                                setTaskAssignmentsUnassigned(
                                                    newValue,
                                                )
                                            }}
                                            label={
                                                <PrimaryText
                                                    typography={
                                                        canManageTaskAssignments
                                                            ? "caption"
                                                            : "text"
                                                    }
                                                >
                                                    {
                                                        POLICIES_CONFIG
                                                            .filtersDisplayMapping
                                                            .taskAssignmentsUnassigned
                                                    }
                                                </PrimaryText>
                                            }
                                        />
                                    ) : null}
                                </div>
                            )

                        case "taskAssignmentAssignability":
                            if (
                                !isBillerSelfAssignEnabled &&
                                !canManageTaskAssignments
                            ) {
                                return null
                            }

                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .taskAssignmentAssignability
                                    }
                                    allowClear
                                    placeholder="Select assignability"
                                    name="taskAssignmentAssignability"
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={Object.values(
                                        PolicyAssignability,
                                    ).map(assignability => ({
                                        value: assignability,
                                        displayValue:
                                            POLICIES_CONFIG
                                                .policyAssignabilityMapping[
                                                assignability
                                            ],
                                    }))}
                                    defaultValue={
                                        props.existingFilters
                                            .taskAssignmentAssignability
                                            ? POLICIES_CONFIG
                                                  .policyAssignabilityMapping[
                                                  props.existingFilters
                                                      .taskAssignmentAssignability
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "taskAssignmentAssignedBy":
                            return (
                                <TagsSelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    placeholder="Select as many as needed"
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .taskAssignmentAssignedBy
                                    }
                                    name="taskAssignmentAssignedBy"
                                    onSelected={(items: string[]) => {
                                        setValue(
                                            "taskAssignmentAssignedBy",
                                            items.map(item => +item),
                                        )
                                    }}
                                    maxTagCount={1}
                                    options={availableOrganizationUsers}
                                    externalValue={
                                        dataWatcher.taskAssignmentAssignedBy as
                                            | string[]
                                            | undefined
                                    }
                                />
                            )

                        case "taskAssignmentStatus":
                            return (
                                <TagsSelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    placeholder="Select as many as needed"
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .taskAssignmentStatus
                                    }
                                    name="taskAssignmentStatus"
                                    onSelected={(items: string[]) => {
                                        setValue(
                                            "taskAssignmentStatus",
                                            items as PolicyAssignmentStatus[],
                                        )
                                    }}
                                    maxTagCount={1}
                                    options={POLICIES_CONFIG.uiAllowedAssignmentStatuses
                                        .map(status => {
                                            const label =
                                                POLICIES_CONFIG.policyAssignmentStatusMapping(
                                                    props.listType,
                                                )[status]
                                            return label
                                                ? {
                                                      label,
                                                      value: status,
                                                  }
                                                : null
                                        })
                                        .filter(
                                            (
                                                option,
                                            ): option is NonNullable<
                                                typeof option
                                            > => option !== null,
                                        )}
                                    externalValue={
                                        dataWatcher.taskAssignmentStatus
                                    }
                                />
                            )

                        case "customFieldAppointmentType":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldAppointmentType
                                    }
                                    name="customFieldAppointmentType"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={
                                        Object.values(
                                            PolicyAssignmentAppointmentType,
                                        ).map(type => ({
                                            label: POLICIES_CONFIG
                                                .policyAssignmentAppointmentTypeMapping[
                                                type
                                            ],
                                            value: type,
                                            displayValue:
                                                POLICIES_CONFIG
                                                    .policyAssignmentAppointmentTypeMapping[
                                                    type
                                                ],
                                        })) || []
                                    }
                                    defaultValue={
                                        props.existingFilters
                                            .customFieldAppointmentType
                                            ? POLICIES_CONFIG
                                                  .policyAssignmentAppointmentTypeMapping[
                                                  props.existingFilters
                                                      .customFieldAppointmentType
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "customFieldRegion":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldRegion
                                    }
                                    name="customFieldRegion"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters.customFieldRegion
                                    }
                                    isLabelStatic
                                    allowClear={true}
                                />
                            )

                        case "customFieldOfficeName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldOfficeName
                                    }
                                    name="customFieldOfficeName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters
                                            .customFieldOfficeName
                                    }
                                    isLabelStatic
                                    allowClear={true}
                                />
                            )

                        case "customFieldCarrierName":
                            return (
                                <InputElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldCarrierName
                                    }
                                    name="customFieldCarrierName"
                                    reactHookControl={control}
                                    reactHookSetValue={setValue}
                                    reactHookValidations={{
                                        maxLength:
                                            FRONT_VALIDATION_CONFIG.maxLength,
                                    }}
                                    reactHookErrors={errors}
                                    defaultValue={
                                        props.existingFilters
                                            .customFieldCarrierName
                                    }
                                    isLabelStatic
                                    allowClear={true}
                                />
                            )

                        case "customFieldWorkflowStatus":
                            return (
                                <SelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldWorkflowStatus
                                    }
                                    name="customFieldWorkflowStatus"
                                    allowClear={true}
                                    reactHookFormRegister={register}
                                    reactHookFormSet={setValue}
                                    reactHookFormUnregister={unregister}
                                    renderedOptions={
                                        Object.values(
                                            PolicyAssignmentWorkflowStatus,
                                        ).map(type => ({
                                            label: POLICIES_CONFIG
                                                .policyAssignmentWorkflowStatusMapping[
                                                type
                                            ],
                                            value: type,
                                            displayValue:
                                                POLICIES_CONFIG
                                                    .policyAssignmentWorkflowStatusMapping[
                                                    type
                                                ],
                                        })) || []
                                    }
                                    defaultValue={
                                        props.existingFilters
                                            .customFieldWorkflowStatus
                                            ? POLICIES_CONFIG
                                                  .policyAssignmentWorkflowStatusMapping[
                                                  props.existingFilters
                                                      .customFieldWorkflowStatus
                                              ]
                                            : undefined
                                    }
                                />
                            )

                        case "checkedByUsers":
                            if (!showFilterByCheckUsers) {
                                return null
                            }

                            return (
                                <div
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                >
                                    {canFilterByCheckUsers && (
                                        <TagsSelectElement
                                            placeholder="Select as many as needed"
                                            label={
                                                POLICIES_CONFIG
                                                    .filtersDisplayMapping
                                                    .checkedByUsers
                                            }
                                            name="checkedByUsers"
                                            onSelected={(items: string[]) => {
                                                setValue(
                                                    "checkedByUsers",
                                                    items.map(item => +item),
                                                )
                                            }}
                                            maxTagCount={1}
                                            options={availableOrganizationUsers}
                                            disabled={
                                                dataWatcher.checkedByMyselfOnly
                                            }
                                            externalValue={
                                                dataWatcher.checkedByMyselfOnly
                                                    ? []
                                                    : (dataWatcher.checkedByUsers as
                                                          | string[]
                                                          | undefined)
                                            }
                                        />
                                    )}

                                    <CheckboxElement
                                        className={
                                            canFilterByCheckUsers
                                                ? "mt-[8px]"
                                                : "mt-[-12px]"
                                        }
                                        name="checkedByMyselfOnly"
                                        reactHookFormRegister={register as any}
                                        reactHookFormUnregister={
                                            unregister as any
                                        }
                                        reactHookFormSet={setValue as any}
                                        watchDefaultCheckedChange
                                        defaultChecked={
                                            props.existingFilters
                                                .checkedByMyselfOnly
                                        }
                                        label={
                                            <PrimaryText typography="text">
                                                {
                                                    POLICIES_CONFIG
                                                        .filtersDisplayMapping
                                                        .checkedByMyselfOnly
                                                }
                                            </PrimaryText>
                                        }
                                    />
                                </div>
                            )

                        case "customFieldCredentialingStatus":
                            return (
                                <TagsSelectElement
                                    key={index}
                                    className={itemFloatingLabelClassName}
                                    placeholder="Select multiple"
                                    label={
                                        POLICIES_CONFIG.filtersDisplayMapping
                                            .customFieldCredentialingStatus
                                    }
                                    name="customFieldCredentialingStatus"
                                    onSelected={(items: string[]) => {
                                        setValue(
                                            "customFieldCredentialingStatus",
                                            items as (keyof typeof PolicyCredentialingStatus)[],
                                        )
                                    }}
                                    maxTagCount={1}
                                    options={(
                                        Object.keys(
                                            PolicyCredentialingStatus,
                                        ) as (keyof typeof PolicyCredentialingStatus)[]
                                    ).map(key => ({
                                        label: PolicyCredentialingStatus[key],
                                        value: key,
                                    }))}
                                    externalValue={
                                        dataWatcher.customFieldCredentialingStatus
                                    }
                                />
                            )

                        default:
                            return null
                    }
                })}
            </>
        )
    }

    return (
        <div className={`relative ${props.className}`}>
            <div
                className={`
                    grid grid-flow-row auto-cols-fr gap-x-4
                    ${props.isWide ? "grid-cols-2" : "grid-cols-1"}
                `}
            >
                {getRenderedFormFields(filtersConfig.main, true)}
            </div>

            {props.withMemberInformation ? (
                <div className="mt-[24px] p-[16px] rounded-[12px] bg-brand-warmLight">
                    {props.isRequired && (
                        <PrimaryText
                            typography="h6"
                            color={
                                props.isDataError
                                    ? NIRVANA_COLORS.brand.error
                                    : undefined
                            }
                        >
                            Enter at least {POLICIES_CONFIG.minMemberDataFields}{" "}
                            fields to make a search from these inputs
                        </PrimaryText>
                    )}

                    <div className="flex md:block mt-[44px]">
                        <InputElement
                            className="
                                mr-[16px] flex-1
                                md:mr-[0px]
                            "
                            label={
                                POLICIES_CONFIG.filtersDisplayMapping
                                    .memberFirstName
                            }
                            name="memberFirstName"
                            reactHookControl={control}
                            reactHookSetValue={setValue}
                            reactHookValidations={{
                                minLength: FRONT_VALIDATION_CONFIG.minLength,
                                maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                            }}
                            reactHookErrors={errors}
                            defaultValue={props.existingFilters.memberFirstName}
                            isLabelStatic
                        />

                        <InputElement
                            className="
                                flex-1
                                md:mt-[44px]
                            "
                            label={
                                POLICIES_CONFIG.filtersDisplayMapping
                                    .memberLastName
                            }
                            name="memberLastName"
                            reactHookControl={control}
                            reactHookSetValue={setValue}
                            reactHookValidations={{
                                minLength: FRONT_VALIDATION_CONFIG.minLength,
                                maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                            }}
                            reactHookErrors={errors}
                            defaultValue={props.existingFilters.memberLastName}
                            isLabelStatic
                        />
                    </div>

                    <div className="flex md:block mt-[44px]">
                        <InputElement
                            className="
                                mr-[16px] flex-1
                                md:mr-[0px] md:mt-[44px]
                            "
                            label={
                                POLICIES_CONFIG.filtersDisplayMapping.memberId
                            }
                            name="memberId"
                            reactHookControl={control}
                            reactHookSetValue={setValue}
                            reactHookValidations={{
                                minLength: FRONT_VALIDATION_CONFIG.minLength,
                                maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                            }}
                            reactHookErrors={errors}
                            defaultValue={props.existingFilters.memberId}
                            isLabelStatic
                        />

                        <DatePickerElement
                            className="
                                flex-1
                                md:mt-[44px]
                            "
                            name="memberDob"
                            label={
                                POLICIES_CONFIG.filtersDisplayMapping.memberDob
                            }
                            defaultValue={
                                props.existingFilters.memberDob
                                    ? moment(props.existingFilters.memberDob)
                                    : undefined
                            }
                            shouldValidate
                            reactHookFormErrors={errors}
                            reactHookFormRegister={register}
                            reactHookFormUnregister={unregister}
                            reactHookFormSet={setValue}
                            maxDate={maxStartDate}
                            allowClear
                            isLabelStatic
                        />
                    </div>
                </div>
            ) : null}

            {filtersConfig.additional.length ||
            filtersConfig.additionalWide?.length ? (
                <div className="mt-[24px]">
                    {!additionalFiltersAlwaysExpanded && (
                        <div
                            className="mt-[40px] pb-[16px] flex items-center cursor-pointer"
                            onClick={() => {
                                setAdditionalFiltersExpanded(
                                    current => !current,
                                )
                            }}
                        >
                            <PrimaryText weight="medium">
                                Additional filters
                            </PrimaryText>

                            <div className="flex-1" />

                            <img src={expandFiltersIcon} alt="expand" />
                        </div>
                    )}

                    {props.isRequired && (
                        <PrimaryText
                            className="mt-[24px]"
                            typography="h6"
                            color={
                                props.isDataError
                                    ? NIRVANA_COLORS.brand.error
                                    : undefined
                            }
                        >
                            {props.withMemberInformation ? "Or enter" : "Enter"}{" "}
                            any of the following inputs
                        </PrimaryText>
                    )}

                    <div
                        className={`
                            ${
                                additionalFiltersAlwaysExpanded
                                    ? "mt-[16px]"
                                    : "mt-[8px]"
                            }
                            ${
                                additionalFiltersAlwaysExpanded ||
                                additionalFiltersExpanded
                                    ? "block"
                                    : "hidden"
                            }
                        `}
                    >
                        {filtersConfig.additionalWide?.length ? (
                            <div
                                className={`
                                    grid grid-flow-row auto-cols-fr gap-4 mb-[24px]
                                    ${
                                        props.isWide
                                            ? "grid-cols-2"
                                            : "grid-cols-1"
                                    }
                                `}
                            >
                                {getRenderedFormFields(
                                    filtersConfig.additionalWide,
                                )}
                            </div>
                        ) : null}

                        <div
                            className={`
                                grid grid-flow-row auto-cols-fr gap-4
                                md:grid-cols-1
                                ${props.isWide ? "grid-cols-3" : "grid-cols-2"}
                            `}
                        >
                            {getRenderedFormFields(filtersConfig.additional)}
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    )
}

interface IPoliciesFiltersFormComponentProps {
    className?: string
    listType: PoliciesListType
    existingFilters: IPoliciesListFiltersData
    isRequired?: boolean
    isWide?: boolean
    isDataError?: boolean
    withMemberInformation?: boolean
    selectedFlags?: OneVerifyFlagType[]
    setSelectedFlags?: Dispatch<SetStateAction<OneVerifyFlagType[] | undefined>>
    flagsDisplayedAmount?: number
    additionalFiltersAlwaysExpanded?: boolean
}
